/**
 * Pure row-shaping helpers for Table.tsx (and, for row identity/cross-referencing, Checklist.tsx),
 * kept in a plain .ts file (no JSX) so they can run under `node --test` directly — Node's built-in
 * TypeScript type-stripping erases type annotations but cannot transform JSX, so anything imported
 * by a `.test.ts` file has to stay JSX-free.
 */

export type Row = Record<string, unknown>;

/**
 * The rows behind a table source, or `null` when the file holds no collection of rows at all.
 *
 * A bare array is the simple case. Everything else in practice is a document that *wraps* its
 * rows, and the wrapper key varies: `{items: [...]}`, but also `{jobs: {<id>: row}}` and
 * `{seen: {<id>: row}}`, which is how the stores in this project persist a merge-only collection
 * (`screening/scored.py`, `jobsearch/store.py`). Accepting only `items` refused those two
 * outright as "not table data" — the error the ranked table hit on every run, since the ranked
 * table's source *is* one of them.
 *
 * So: find the one collection in the document. A collection is an array of rows, or a map whose
 * values are all rows. `items` wins when present, since a document that names it means it;
 * otherwise exactly one candidate has to stand out, because picking one of two would be guessing
 * which half of the file the reader meant. Scalar siblings (`schema_version`, `generated_at`) are
 * not collections and never get in the way.
 *
 * **In a map, the key is the row's identity**, so it fills in `id` for rows that carry none —
 * `jobs/scored.json`'s entries are filed under their content hash and carry `job_id`, not `id`,
 * and without this every `ids` filter and every React key would fall back to the row's position.
 * A row with its own `id` keeps it.
 */
export function normalizeRows(data: unknown): Row[] | null {
	if (Array.isArray(data)) return data as Row[];
	if (!isRecord(data)) return null;

	const candidates = Object.entries(data).filter(([, value]) => asCollection(value) !== null);
	if (candidates.length === 0) return null;
	const chosen = candidates.find(([key]) => key === "items") ?? (candidates.length === 1 ? candidates[0] : undefined);
	return chosen ? asCollection(chosen[1]) : null;
}

/** An array of rows, or a map of id -> row (the key filling in a missing `id`). Null if neither. */
function asCollection(value: unknown): Row[] | null {
	if (Array.isArray(value)) return value.every(isRecord) ? (value as Row[]) : null;
	if (!isRecord(value)) return null;
	const entries = Object.entries(value);
	if (!entries.every(([, row]) => isRecord(row))) return null;
	return entries.map(([key, row]) => ({ id: key, ...(row as Row) }));
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** Keeps rows whose `id` is in `ids` (compared as strings, so numeric and string ids both match). Undefined `ids` means no filtering. */
export function filterByIds(rows: Row[], ids: string[] | undefined): Row[] {
	if (!ids) return rows;
	const wanted = new Set(ids.map(String));
	return rows.filter((row) => wanted.has(String(row.id)));
}

/**
 * A fallback column whose first-row value is longer than this is a body of text, not a cell: a
 * scraped posting's `description`, say. It is skipped unless asked for by name, because one such
 * field turns every row of a table into a paragraph and buries the fields the user compares.
 */
const LONG_TEXT_CHARS = 120;

/** Explicit `columns` win; otherwise the first row's own key order, minus long-text fields. Empty when there are no rows to take keys from. */
export function pickColumns(rows: Row[], columns: string[] | undefined): string[] {
	if (columns && columns.length > 0) return columns;
	if (rows.length === 0) return [];
	const first = rows[0]!;
	return Object.keys(first).filter((key) => {
		// `id` addresses a row, it does not describe one, and normalizeRows puts a content hash
		// there for every row of a keyed map. Name it in `columns` to see it.
		if (key === "id") return false;
		const value = first[key];
		return !(typeof value === "string" && value.length > LONG_TEXT_CHARS);
	});
}

/** A cell renders as a link when its value looks like an http(s) URL. */
export function isUrlValue(value: unknown): value is string {
	return typeof value === "string" && /^https?:\/\//i.test(value);
}

/** Row identity: `id` when present (string or number, always compared as a string), else the row's position in whatever array it came from. Shared by Table.tsx (React keys) and Checklist.tsx (React keys AND cross-referencing a submitted answer's ids back to rows — see `resolveAnsweredRows`), so both treat "which row is this" identically. */
export function rowId(row: Row, index: number): string {
	const id = row.id;
	return typeof id === "string" || typeof id === "number" ? String(id) : String(index);
}

/**
 * The ids a checklist starts with ticked: every row it shows when its `checked` argument is `"all"`,
 * none otherwise. For a list whose usual answer is all of it, so agreeing is one tap.
 */
export function preticked(checked: unknown, rows: Row[]): string[] {
	return checked === "all" ? rows.map(rowId) : [];
}

export interface ResolvedAnswer {
	id: string;
	row: Row | undefined;
}

/**
 * Cross-references a submitted Checklist answer's ids against the rows currently available from
 * `source`, preserving every id from `answeredIds` in the returned array — `row` is `undefined`
 * for an id that does not match any current row (the source has changed, or can't be resolved at
 * all right now), but the id itself is never dropped. This is what lets Checklist.tsx render a
 * submitted-but-now-unmatched selection honestly (as a bare id, explicitly marked) instead of
 * silently omitting it, which is the one thing this project's "不要静默失败" rule forbids outright.
 */
export function resolveAnsweredRows(answeredIds: string[], rows: Row[]): ResolvedAnswer[] {
	// Deliberately NOT rowId(): an index is a correct React key and a wrong answer identifier.
	// Resolving "1" positionally means that once the source is reordered or rewritten, whatever
	// row now sits at index 1 is shown checked, as the user's selection, with every guard
	// satisfied and no warning anywhere — a specific, plausible-looking, wrong answer. Matching
	// only real ids sends id-less rows down the already-built unresolved path instead, which
	// says plainly that the selection cannot be tied to a row any more. Same principle as the
	// Form fix: a blank recovers nothing, a substituted default asserts something false.
	const byId = new Map<string, Row>();
	for (const row of rows) {
		const id = row.id;
		if (typeof id === "string" || typeof id === "number") byId.set(String(id), row);
	}
	return answeredIds.map((id) => ({ id, row: byId.get(id) }));
}

/**
 * Coerces a `components.yaml`-declared array argument (`ids`, `columns`) to `string[]`.
 * `execute()` never validates a tool call's arguments against its JSON Schema (see
 * `src/runtime/components.ts`), so a model could in principle send numbers where the schema
 * says strings — stringifying elements is more forgiving than rejecting the whole table over it.
 * Anything that is not an array at all is treated as "not provided" rather than a hard error:
 * `ids`/`columns` are optional refinements, not load-bearing for whether the table can render.
 */
export function asStringArray(value: unknown): string[] | undefined {
	if (!Array.isArray(value)) return undefined;
	return value.map((v) => String(v));
}
